import { useEffect } from 'react';
import { useLocation } from 'wouter';

/**
 * Enhanced ScrollRestoration component to reset scroll position on page navigation
 * This component should be included in the application layout
 * This enhanced version handles both navigation and history operations
 */
export function ScrollRestoration() {
  const [location] = useLocation();
  
  // Reset scroll position when location changes (navigation)
  useEffect(() => {
    console.log('Location changed to:', location);
    
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    
    // For browsers that might not respect the initial scroll, 
    // add a tiny delay and scroll again
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
    
    return () => clearTimeout(timeoutId);
  }, [location]);

  // For history navigation (back/forward buttons)
  useEffect(() => {
    const handleNavigation = () => {
      console.log('History navigation detected');
      window.scrollTo(0, 0);
      
      // Double-check after a tiny delay
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 10);
    };
    
    // Listen for both popstate (browser back/forward) and hashchange events
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);
    
    // Clean up
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
    };
  }, []);
  
  // This handler ensures clicks on links also reset scroll
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if this is a link or inside a link
      const linkElement = target.closest('a');
      
      if (linkElement && linkElement.getAttribute('href')?.startsWith('/')) {
        console.log('Internal link click detected');
        // It's an internal link, scroll to top immediately
        window.scrollTo(0, 0);
      }
    };
    
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return null;
}

export default ScrollRestoration;